import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getBreakpoint } from '@/scripts/tools/constants';
import { debounce } from 'lodash'

document.addEventListener('alpine:init', () => {
    Alpine.data('expertiseList', () => ({
        init() {
            this.$nextTick(() => {
                initExpertiseList();
            });
        },
    }))
})

// Creates a clone of the list with the appropriate classes
function createListClone(originalList, position, otherClasses) {
    const clone = originalList.cloneNode(true);
    clone.className = `inpExpertiseList__word-stack-clone ${position} ${otherClasses}`;
    clone.setAttribute('aria-hidden', 'true');
    return clone;
}

// Creates a ScrollTrigger with common parameters
function createScrollTrigger(config) {
    return ScrollTrigger.create({
        scrub: true,
        ...config
    });
}

// Initializes the list clones
function initializeClones(content, originalList) {
    if (window.innerWidth < getBreakpoint('lg')) {
        return;
    }

    const existingClones = content.querySelectorAll('.inpExpertiseList__word-stack-clone');
    existingClones.forEach(clone => clone.remove());

    const otherClasses = Array.from(originalList.classList)
        .filter(c => c !== 'inpExpertiseList__word-stack')
        .join(' ');

    const cloneBefore = createListClone(originalList, 'before', otherClasses);
    content.insertBefore(cloneBefore, originalList);

    const cloneAfter = createListClone(originalList, 'after', otherClasses);
    content.appendChild(cloneAfter);
}

// Creates or retrieves the endPin element 
function getOrCreateEndPin(wrapper, content) {
    if (window.innerWidth < getBreakpoint('lg')) {
        return null;
    }

    let endPin = wrapper.querySelector('.inpExpertiseList__end-pin');

    if (!endPin) {
        endPin = document.createElement('div');
        endPin.classList.add('inpExpertiseList__end-pin');
        endPin.setAttribute('aria-hidden', 'true');
        endPin.textContent = ".";
        content.appendChild(endPin);
    }

    return endPin;
}

// Initializes opacity animations
function initializeOpacityAnimation(firstListItem, itemsNotFirst, itemsNotLast) {
    gsap.set(firstListItem, { opacity: 1 });

    return gsap.timeline()
        .to(itemsNotFirst, {
            opacity: 1,
            stagger: 0.5
        })
        .to(itemsNotLast, {
            opacity: 0.15,
            stagger: 0.5
        }, 0);
}

// Creates ScrollTriggers for a wrapper
function createScrollTriggers(wrapper, firstListItem, lastListItem, endPin, mask, titleElement, animation) {
    createScrollTrigger({
        trigger: firstListItem,
        start: "center center",
        endTrigger: lastListItem,
        end: "center center",
        animation: animation
    });

    if (window.innerWidth >= getBreakpoint('lg') && mask && endPin) {
        createScrollTrigger({
            trigger: wrapper,
            start: "top top",
            endTrigger: endPin,
            end: "center center",
            pin: mask
        });
    }

    if (window.innerWidth >= getBreakpoint('lg') && titleElement) {
        createScrollTrigger({
            trigger: firstListItem,
            start: "center center",
            endTrigger: lastListItem,
            end: "center center",
            pin: titleElement
        });
    }
}

function initExpertiseList() {
    gsap.registerPlugin(ScrollTrigger);

    const wrappers = gsap.utils.toArray('.inpExpertiseList__wrapper');

    wrappers.forEach(wrapper => {
        if (wrapper.classList.contains('expertise-list-init')) return;
        wrapper.classList.add('expertise-list-init');

        const content = wrapper.querySelector('.inpExpertiseList__words-container');
        const originalList = content?.querySelector('ul.inpExpertiseList__word-stack');
        
        if (!content || !originalList) return;
        
        const firstListItem = originalList.querySelector('li:first-of-type');
        const lastListItem = originalList.querySelector('li:last-of-type');
        
        if (!firstListItem || !lastListItem) return;

        const itemsNotFirst = originalList.querySelectorAll('li:not(:first-of-type)');
        const itemsNotLast = originalList.querySelectorAll('li:not(:last-of-type)');
        const mask = wrapper.querySelector('.inpExpertiseList__mask');
        const titleElement = wrapper.querySelector('.inpExpertiseList__text');

        initializeClones(content, originalList);
        
        const endPin = getOrCreateEndPin(wrapper, content);
        
        if (window.innerWidth >= getBreakpoint('lg')) {
            wrapper.classList.add('pin-ready');
        }

        const animation = initializeOpacityAnimation(firstListItem, itemsNotFirst, itemsNotLast);
        createScrollTriggers(wrapper, firstListItem, lastListItem, endPin, mask, titleElement, animation);
    });
}
